import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  shouldVirtualizeTechnicianSession,
  TECHNICIAN_SESSION_VIRTUAL_ENABLED,
} from '../utils/technicianSessionVirtual';
import './TechnicianSessionVirtualList.css';

/**
 * Virtualiza itens de sessão longa (cidades ou forecast groups) no técnico.
 * Live (Em deslocamento / Em serviço): sempre plain.
 * Scroll próprio quando virtualiza (evita scrollMargin no column-content).
 */
export function TechnicianSessionVirtualList({
  groupName,
  items = [],
  enabled = TECHNICIAN_SESSION_VIRTUAL_ENABLED,
  overscan = 4,
  estimateSize,
  getItemKey,
  children,
}) {
  const list = Array.isArray(items) ? items : [];
  const parentRef = useRef(null);
  const useVirtual =
    enabled && shouldVirtualizeTechnicianSession(groupName, list.length);

  const rowVirtualizer = useVirtualizer({
    count: useVirtual ? list.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      typeof estimateSize === 'function' ? estimateSize(list[index], index) : 80,
    overscan,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (el) => el?.getBoundingClientRect().height
        : undefined,
  });

  const resolveKey = (item, index) => {
    if (typeof getItemKey === 'function') return getItemKey(item, index);
    return item?.key ?? item?.cidade ?? item?.id ?? index;
  };

  if (!useVirtual) {
    return (
      <div className="service-orders-container" ref={parentRef}>
        {list.map((item, index) => (
          <React.Fragment key={resolveKey(item, index)}>
            {typeof children === 'function' ? children(item, index) : null}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      className="service-orders-container service-orders-container--virtual-session"
      ref={parentRef}
    >
      <div
        className="technician-session-virtual-spacer"
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const item = list[virtualRow.index];
          return (
            <div
              key={resolveKey(item, virtualRow.index)}
              className="technician-session-virtual-item"
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {typeof children === 'function' ? children(item, virtualRow.index) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TechnicianSessionVirtualList;
